/**
 * Cation / CEC structure triangle.
 *
 * Geometry: exact coloured regions from the structure-triangle asset.
 * Colours: Flaha workspace theme in-app; high-contrast print theme for PDF capture.
 */
import { Box, Chip, Stack, Typography, Tooltip } from "@mui/material";
import type { StructureAnalysisBlock } from "@flaha/shared-types";
import type { ScientificChartData } from "../types/ScientificChartTypes";
import { SpecialNote } from "../../../results/components/SpecialNote";
import { useChartTheme } from "../utils/useChartTheme";
import {
	APEX_MG,
	APEX_RESIDUAL,
	STRUCTURE_SVG_SIZE,
	barycentricToStructureSvg,
	computeCecSaturation,
	edgeTick,
	pointInsideStructureTriangle,
} from "../utils/structureTriangleGeometry";
import {
	STRUCTURE_REGIONS,
	STRUCTURE_ZONE_VIEWBOX,
	TRIANGLE_OUTLINE_D,
	type StructureRegionRole,
} from "../utils/structureTriangleZones";
import type { ChartTheme } from "../utils/chartTheme";

const VERDICT_COLOR: Record<string, "success" | "warning" | "error" | "default"> =
	{
		Balanced: "success",
		"Calcium Excess": "warning",
		"Magnesium Excess": "warning",
		"Potassium Excess": "warning",
		"Calcium Deficient": "error",
		"Magnesium Deficient": "error",
		"Potassium Deficient": "error",
	};

const LEFT_TICKS = [100, 95, 90, 85, 75, 65] as const;
const RIGHT_TICKS = [0, 6, 10] as const;

function regionFill(
	theme: ChartTheme,
	role: StructureRegionRole
): string {
	switch (role) {
		case "moderate":
			return theme.structureModerate;
		case "moderateSoft":
			return theme.structureModerateSoft;
		case "poor":
			return theme.structurePoor;
		case "reasonable":
			return theme.structureReasonable;
		case "good":
			return theme.structureGood;
		case "optimal":
			return theme.structureOptimal;
	}
}

interface CationBalanceChartProps {
	chartData: ScientificChartData<StructureAnalysisBlock | null>;
}

export function CationBalanceChart({ chartData }: CationBalanceChartProps) {
	const theme = useChartTheme();
	const { data: structure, accessibilitySummary } = chartData;
	const saturation = structure ? computeCecSaturation(structure) : null;
	const point = saturation
		? barycentricToStructureSvg(
				saturation.caSat,
				saturation.mgSat,
				saturation.residual
			)
		: null;
	const pointOk =
		point != null && pointInsideStructureTriangle(point.x, point.y);
	const verdictColor: "success" | "warning" | "error" | "default" =
		(structure?.classification
			? VERDICT_COLOR[structure.classification]
			: undefined) ?? "default";

	const zoneLegend: Array<{ label: string; color: string }> = [
		{ label: "Optimal structure", color: theme.structureOptimal },
		{ label: "Good structure", color: theme.structureGood },
		{ label: "Reasonable structure", color: theme.structureReasonable },
		{ label: "Moderate structure", color: theme.structureModerate },
		{ label: "Poor structure", color: theme.structurePoor },
	];

	// Print: slightly larger type for A4 readability
	const isPrint = theme.structureLabel === "#241A14";
	const tickSize = isPrint ? 15 : 13;
	const labelSize = isPrint ? 17 : 15;
	const legendSize = isPrint ? 15 : 13;

	return (
		<Stack spacing={1.5} aria-label={accessibilitySummary}>
			<Box
				component="svg"
				data-chart-core="cationBalance"
				viewBox={`0 0 ${STRUCTURE_ZONE_VIEWBOX} ${STRUCTURE_ZONE_VIEWBOX}`}
				role="img"
				data-testid="cation-balance-chart"
				sx={{
					width: "100%",
					height: "auto",
					maxWidth: STRUCTURE_SVG_SIZE,
					backgroundColor: theme.structureBg,
				}}
			>
				{STRUCTURE_REGIONS.map((region) => (
					<path
						key={region.id}
						data-region={region.id}
						d={region.d}
						fill={regionFill(theme, region.role)}
						fillRule="evenodd"
						stroke={
							region.stroke
								? isPrint
									? "rgba(36,26,20,0.35)"
									: "rgba(255,255,255,0.55)"
								: undefined
						}
						strokeWidth={region.stroke ? 0.8 : undefined}
					/>
				))}

				<path
					d={TRIANGLE_OUTLINE_D}
					fill="none"
					stroke={theme.structureOutline}
					strokeWidth={isPrint ? 2.25 : 1.75}
					strokeLinejoin="round"
				/>

				{LEFT_TICKS.map((ca) => {
					const p = edgeTick("left", ca);
					return (
						<text
							key={`L${ca}`}
							x={p.x - 8}
							y={p.y + 4}
							textAnchor="end"
							fontSize={tickSize}
							fontWeight={700}
							fill={theme.structureTick}
						>
							{ca}
						</text>
					);
				})}
				{RIGHT_TICKS.map((ca) => {
					const p = edgeTick("right", ca);
					const dx = ca === 0 ? 10 : 8;
					const dy = ca === 0 ? -6 : 4;
					return (
						<text
							key={`R${ca}`}
							x={p.x + dx}
							y={p.y + dy}
							textAnchor="start"
							fontSize={tickSize}
							fontWeight={700}
							fill={theme.structureTick}
						>
							{ca}
						</text>
					);
				})}
				<text
					x={APEX_RESIDUAL.x - 6}
					y={APEX_RESIDUAL.y + 22}
					textAnchor="end"
					fontSize={tickSize}
					fontWeight={700}
					fill={theme.structureTick}
				>
					30
				</text>
				<text
					x={APEX_MG.x + 6}
					y={APEX_MG.y + 22}
					textAnchor="start"
					fontSize={tickSize}
					fontWeight={700}
					fill={theme.structureTick}
				>
					70
				</text>
				{(() => {
					const tip = edgeTick("right", 100);
					return (
						<text
							x={tip.x + 14}
							y={tip.y + 2}
							textAnchor="start"
							fontSize={tickSize - 1}
							fontWeight={700}
							fill={theme.structureTick}
						>
							0
						</text>
					);
				})()}

				<text
					x={72}
					y={288}
					textAnchor="middle"
					fontSize={labelSize}
					fontWeight={700}
					fill={theme.structureLabel}
				>
					Ca-CEC
				</text>
				<text
					x={72}
					y={306}
					textAnchor="middle"
					fontSize={labelSize - 2}
					fill={theme.structureLabelMuted}
				>
					(%)
				</text>
				<path
					d="M100 330 L132 330 L118 348 Z"
					fill={theme.structureArrow}
				/>
				<path
					d="M88 330 H118"
					stroke={theme.structureArrow}
					strokeWidth={2.2}
					strokeLinecap="round"
				/>

				<text
					x={770}
					y={288}
					textAnchor="middle"
					fontSize={labelSize}
					fontWeight={700}
					fill={theme.structureLabel}
				>
					Mg-CEC
				</text>
				<text
					x={770}
					y={306}
					textAnchor="middle"
					fontSize={labelSize - 2}
					fill={theme.structureLabelMuted}
				>
					(%)
				</text>
				<path
					d="M742 318 L718 348 L752 348 Z"
					fill={theme.structureArrow}
				/>

				<text
					x={(APEX_RESIDUAL.x + APEX_MG.x) / 2}
					y={APEX_RESIDUAL.y + 48}
					textAnchor="middle"
					fontSize={labelSize - 1}
					fontWeight={700}
					fill={theme.structureLabel}
				>
					K-, Na-, H-, Al-, Fe-CEC
				</text>
				<text
					x={(APEX_RESIDUAL.x + APEX_MG.x) / 2}
					y={APEX_RESIDUAL.y + 66}
					textAnchor="middle"
					fontSize={labelSize - 3}
					fill={theme.structureLabelMuted}
				>
					(%)
				</text>
				<path
					d="M250 610 L270 582 L290 610 Z"
					fill={theme.structureArrow}
				/>

				{point && saturation ? (
					<Tooltip
						title={`Ca-sat ${saturation.caSat.toFixed(1)}% · Mg-sat ${saturation.mgSat.toFixed(1)}% · Residual ${saturation.residual.toFixed(1)}%`}
						arrow
					>
						<g style={{ cursor: "help" }} data-testid="structure-point">
							<circle
								cx={point.x}
								cy={point.y}
								r={14}
								fill={theme.structureSampleHalo}
							/>
							<circle
								cx={point.x}
								cy={point.y}
								r={9}
								fill={theme.structureSample}
								stroke={theme.structureSampleRing}
								strokeWidth={3}
							/>
						</g>
					</Tooltip>
				) : null}

				<g transform="translate(90, 700)">
					{zoneLegend.map((z, i) => (
						<g key={z.label} transform={`translate(0, ${i * 26})`}>
							<rect
								x={0}
								y={0}
								width={18}
								height={16}
								rx={2}
								fill={z.color}
								stroke={theme.structureOutline}
								strokeWidth={1}
							/>
							<text
								x={28}
								y={13}
								fontSize={legendSize}
								fontWeight={700}
								fill={theme.structureLabel}
							>
								{z.label}
							</text>
						</g>
					))}
					<g transform="translate(300, 52)">
						<circle
							cx={8}
							cy={8}
							r={7}
							fill={theme.structureSample}
							stroke={theme.structureSampleRing}
							strokeWidth={2}
						/>
						<text
							x={24}
							y={12}
							fontSize={legendSize}
							fontWeight={700}
							fill={theme.structureLabel}
						>
							Your sample (CEC saturation)
						</text>
					</g>
				</g>

				{point && !pointOk ? (
					<text
						x={STRUCTURE_ZONE_VIEWBOX / 2}
						y={STRUCTURE_ZONE_VIEWBOX - 14}
						textAnchor="middle"
						fontSize={13}
						fontWeight={700}
						fill={theme.wiltingPoint}
					>
						Note: point near boundary — check Ca / Mg / CEC inputs
					</text>
				) : null}
			</Box>

			{/* Below-chart chrome — hidden from PDF snapshot capture */}
			<Box data-print-hide>
				{structure ? (
					<Stack spacing={1}>
						<Stack
							direction="row"
							spacing={1}
							alignItems="center"
							flexWrap="wrap"
							useFlexGap
						>
							<Chip
								size="small"
								color={verdictColor}
								label={structure.classification ?? "Unclassified"}
							/>
							<Typography
								variant="body2"
								sx={{ color: "workspace.textSecondary" }}
							>
								{saturation
									? `Ca-sat ${saturation.caSat.toFixed(1)}% · Mg-sat ${saturation.mgSat.toFixed(1)}% · Residual ${saturation.residual.toFixed(1)}% · CEC ${(structure.cec ?? 0).toFixed(1)} ${structure.unit}`
									: `CEC missing — point cannot be placed.`}
							</Typography>
						</Stack>
						{structure.normalized ? (
							<Typography
								variant="caption"
								sx={{ color: "workspace.textMuted" }}
							>
								BCSR share of bases (Ca+Mg+K): Ca{" "}
								{structure.normalized.ca.toFixed(1)}% · Mg{" "}
								{structure.normalized.mg.toFixed(1)}% · K{" "}
								{structure.normalized.k.toFixed(1)}%
							</Typography>
						) : null}
						<SpecialNote
							kind="methodology"
							title="How the sample point is placed"
							dense
							data-testid="structure-disclaimer"
							detail={structure.disclaimer}
						>
							Zone shapes match the original CEC structure triangle. Marker uses
							CEC saturation (Ca/CEC, Mg/CEC, residual).
						</SpecialNote>
					</Stack>
				) : (
					<Typography variant="body2" color="text.secondary">
						No cation data available.
					</Typography>
				)}
			</Box>
		</Stack>
	);
}
